import maplibregl, { Map, Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useEffect, useRef } from 'react';

type Props = {
  lat?: number | null;
  lon?: number | null;
  height?: number;
  zoom?: number;
};

export default function MapView({ lat, lon, height = 280, zoom = 12 }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const markerRef = useRef<Marker | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const center: [number, number] = [lon ?? -3.4360, lat ?? 55.3781]; // default UK center
    const osmStyle: any = {
      version: 8,
      sources: {
        osm: {
          type: 'raster',
          tiles: [
            'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
            'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
            'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
          ],
          tileSize: 256,
          attribution: '© OpenStreetMap contributors',
        },
      },
      layers: [
        { id: 'osm', type: 'raster', source: 'osm' },
      ],
    };
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: osmStyle,
      center,
      zoom: lat != null && lon != null ? 15 : zoom,
      attributionControl: { compact: true },
      maxZoom: 19,
      minZoom: 2,
    });
    mapRef.current = map;

    if (lat != null && lon != null) {
      const lngLat: [number, number] = [lon, lat];
      const el = document.createElement('div');
      el.style.width = '14px';
      el.style.height = '14px';
      el.style.borderRadius = '50%';
      el.style.background = '#ef4444';
      el.style.border = '2px solid #fff';
      el.style.boxShadow = '0 0 0 2px rgba(0,0,0,0.2)';
      markerRef.current = new maplibregl.Marker({ element: el }).setLngLat(lngLat).addTo(map);
    }

    return () => {
      markerRef.current?.remove();
      mapRef.current?.remove();
      markerRef.current = null;
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (lat != null && lon != null && Number.isFinite(lat) && Number.isFinite(lon)) {
      const lngLat = [lon, lat] as [number, number];
      map.easeTo({ center: lngLat, zoom: Math.max(map.getZoom(), 15) });
      if (!markerRef.current) {
        const el = document.createElement('div');
        el.style.width = '14px';
        el.style.height = '14px';
        el.style.borderRadius = '50%';
        el.style.background = '#ef4444';
        el.style.border = '2px solid #fff';
        el.style.boxShadow = '0 0 0 2px rgba(0,0,0,0.2)';
        markerRef.current = new maplibregl.Marker({ element: el }).setLngLat(lngLat).addTo(map);
      } else {
        markerRef.current.setLngLat(lngLat);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lon, zoom]);

  return (
    <div
      ref={containerRef}
      className="rounded-md border overflow-hidden"
      style={{ height }}
    />
  );
}


